import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, ChevronDown, Check, Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/hooks/useAuth';
import { useImpersonation } from '@/shared/context/useImpersonation';
import { authApi } from '@/modules/auth/api/authApi';
import { authKeys } from '@/modules/auth/queryKeys';
import { agencyApi } from '@/modules/agency/api/agencyApi';
import { agencyKeys } from '@/modules/agency/queryKeys';
import { AUTH_CONFIG } from '@/config/auth';
import styles from './PracticeSwitcher.module.css';

interface PracticeOption {
  id: string;
  name: string;
}

interface PracticeSwitcherProps {
  fallbackName?: string;
}

export function PracticeSwitcher({ fallbackName }: PracticeSwitcherProps) {
  const { user, login } = useAuth();
  const { impersonatedPractice, startImpersonation } = useImpersonation();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [search, setSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Regular users: fetch practices from auth endpoint
  const { data: userPractices } = useQuery({
    queryKey: authKeys.practices(),
    queryFn: async () => {
      const res = await authApi.getUserPractices();
      return res.data;
    },
    enabled: !!user && !user.isAgency,
  });

  // Agency users: fetch all managed practices (backend caps limit at 2000)
  const { data: agencyPracticesData } = useQuery({
    queryKey: agencyKeys.allPractices(),
    queryFn: async () => {
      const res = await agencyApi.getPractices({ limit: 2000 });
      return res.data;
    },
    enabled: !!user?.isAgency,
  });

  const isAgency = !!user?.isAgency;

  const practices: PracticeOption[] = useMemo(() => {
    if (!user) return [];
    if (isAgency) {
      return (agencyPracticesData ?? [])
        .filter((p) => p.status !== 'inactive' && !p.snoozedAt)
        .map((p) => ({ id: p.id, name: p.name }));
    }
    return (userPractices ?? []).map((p) => ({ id: p.id, name: p.name }));
  }, [user, isAgency, agencyPracticesData, userPractices]);

  const sortedPractices = useMemo(
    () =>
      [...practices].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
      ),
    [practices],
  );

  const filteredPractices = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedPractices;
    return sortedPractices.filter((p) => p.name.toLowerCase().includes(q));
  }, [sortedPractices, search]);

  useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => searchInputRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    setSearch('');
  }, [isOpen]);

  if (!user) return null;

  const currentId = isAgency ? impersonatedPractice?.id : user.practiceId;
  const displayName = isAgency
    ? (impersonatedPractice?.name ?? fallbackName ?? 'Select Practice')
    : (practices.find((p) => p.id === currentId)?.name ?? fallbackName ?? 'Practice');
  const hasMultiple = practices.length > 1;

  const handleSwitch = async (practice: PracticeOption) => {
    if (practice.id === currentId || isSwitching) return;

    setIsSwitching(true);
    try {
      if (isAgency) {
        startImpersonation({ id: practice.id, name: practice.name });
        window.location.href = '/';
      } else {
        const refreshToken = localStorage.getItem(AUTH_CONFIG.refreshTokenKey);
        if (!refreshToken) return;

        const response = await authApi.switchPractice(practice.id, refreshToken);
        const { access_token, refresh_token, user: userData } = response.data;

        login(access_token, refresh_token, userData);
        window.location.reload();
      }
    } catch {
      setIsSwitching(false);
    }
  };

  if (!hasMultiple) {
    return (
      <div className={styles.single}>
        <Building2 size={16} className={styles.icon} />
        <span className={styles.practiceName}>{displayName}</span>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <button className={styles.trigger} onClick={() => setIsOpen(!isOpen)} disabled={isSwitching}>
        <Building2 size={16} className={styles.icon} />
        <span className={styles.practiceName}>{displayName}</span>
        <ChevronDown size={14} className={styles.chevron} />
      </button>

      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdown}>
            <div className={styles.searchRow}>
              <Search size={14} className={styles.searchIcon} />
              <input
                ref={searchInputRef}
                type="text"
                className={styles.searchInput}
                placeholder="Search practices…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                }}
              />
            </div>
            <div className={styles.list}>
              {filteredPractices.length === 0 ? (
                <div className={styles.emptyState}>No practices match “{search}”</div>
              ) : (
                filteredPractices.map((practice) => (
                  <button
                    key={practice.id}
                    className={styles.dropdownItem}
                    onClick={() => handleSwitch(practice)}
                  >
                    <span className={styles.practiceItemName}>{practice.name}</span>
                    {practice.id === currentId && <Check size={14} className={styles.check} />}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
